/**
 * Client-side reading of the dependency graph the action lists already carry.
 *
 * Every action arrives with `depends_on`, so the whole graph for a scope is
 * reconstructable without another request. That is what lets the "Blocked by"
 * picker offer only legal predecessors instead of letting the user pick a cycle
 * and reading the rejection back off a toast.
 *
 * The authority on what is legal is still the server — see coms
 * `actionDependencies.ts`, which runs the same reachability check on write.
 */

export type ActionDependencyBlocker = {
  action_id: string;
  name: string;
  status: string;
};

export type ActionDependencyNode = {
  action_id: string;
  name?: string;
  archived?: boolean;
  depends_on?: string[] | null;
  blocked_by?: ActionDependencyBlocker[] | null;
};

/** Predecessor to its successors, the direction dependencies flow. */
function successorsByPredecessor(
  actions: ActionDependencyNode[]
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  actions.forEach((action) => {
    (action.depends_on ?? []).forEach((predecessor) => {
      const existing = map.get(predecessor) ?? [];
      existing.push(action.action_id);
      map.set(predecessor, existing);
    });
  });
  return map;
}

/** Everything that already waits on this action, however indirectly. */
function descendantsOf(
  actionId: string,
  actions: ActionDependencyNode[]
): Set<string> {
  const successors = successorsByPredecessor(actions);
  const seen = new Set<string>();
  const queue = [...(successors.get(actionId) ?? [])];

  while (queue.length > 0) {
    const current = queue.shift() as string;
    if (seen.has(current)) continue;
    seen.add(current);
    queue.push(...(successors.get(current) ?? []));
  }

  return seen;
}

/**
 * The actions that could legally block `actionId`, as picker options.
 *
 * Anything downstream of this action is left out: making it a predecessor would
 * put the action behind itself. An unsaved action has no id yet and so nothing
 * downstream, meaning every other action is fair game.
 */
export function dependencyOptions(
  actionId: string | null | undefined,
  actions: ActionDependencyNode[]
): { label: string; value: string }[] {
  const excluded = actionId
    ? descendantsOf(actionId, actions)
    : new Set<string>();

  return actions
    .filter(
      (action) =>
        !action.archived &&
        action.action_id &&
        action.action_id !== actionId &&
        !excluded.has(action.action_id)
    )
    .map((action) => ({
      label: action.name || 'Untitled task',
      value: action.action_id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** The status column is narrow, so a long task name is elided into the tooltip. */
const MAX_BADGE_NAME = 24;

function elide(name: string): string {
  return name.length > MAX_BADGE_NAME
    ? `${name.slice(0, MAX_BADGE_NAME - 1)}…`
    : name;
}

/**
 * How the table says what is in the way: one blocker by name, several by count,
 * with the full list in the tooltip either way.
 */
export type ActionDependencyLink = {
  action_id: string;
  name: string;
  status?: string | null;
};

export type ActionDependencyChain = {
  predecessors: ActionDependencyLink[];
  successors: ActionDependencyLink[];
  blockingCount: number;
};

function nameOf(action: ActionDependencyNode): string {
  return action.name?.trim() || 'Untitled task';
}

/** Open successors only — closed work is no longer waiting. */
export function blockingCount(
  actionId: string,
  actions: ActionDependencyNode[]
): number {
  const successors = successorsByPredecessor(actions);
  const byId = new Map(actions.map((action) => [action.action_id, action]));
  return (successors.get(actionId) ?? []).filter((id) => {
    const successor = byId.get(id);
    return successor && !successor.archived && successor.blocked_by?.length;
  }).length;
}

/**
 * Immediate neighbours of this task in the graph the list already carries.
 *
 * Only the next hop either way: a full walk is what the server uses to reject
 * cycles, but the UI question is "what is in the way, and what is waiting".
 */
export function dependencyChain(
  actionId: string,
  actions: ActionDependencyNode[]
): ActionDependencyChain {
  const self = actions.find((action) => action.action_id === actionId);
  const byId = new Map(actions.map((action) => [action.action_id, action]));
  const successors = successorsByPredecessor(actions);
  const openBlockers = new Map(
    (self?.blocked_by ?? []).map((blocker) => [blocker.action_id, blocker])
  );

  const predecessors = (self?.depends_on ?? []).map((id) => {
    const blocker = openBlockers.get(id);
    const action = byId.get(id);
    return {
      action_id: id,
      name: blocker?.name || (action ? nameOf(action) : 'Untitled task'),
      status: blocker?.status ?? null,
    };
  });

  const successorLinks = (successors.get(actionId) ?? [])
    .map((id) => byId.get(id))
    .filter((action): action is ActionDependencyNode =>
      Boolean(action && !action.archived)
    )
    .map((action) => ({
      action_id: action.action_id,
      name: nameOf(action),
      status: null,
    }));

  return {
    predecessors,
    successors: successorLinks,
    blockingCount: successorLinks.filter((link) =>
      Boolean(byId.get(link.action_id)?.blocked_by?.length)
    ).length,
  };
}

export function describeBlockers(
  blockers: ActionDependencyBlocker[] | null | undefined
): {
  label: string;
  title: string;
} | null {
  if (!blockers?.length) return null;

  const names = blockers.map((blocker) => blocker.name || 'Untitled task');
  return {
    label:
      names.length === 1
        ? `Blocked by ${elide(names[0])}`
        : `Blocked by ${names.length} tasks`,
    title: `Waiting on ${names.join(', ')}`,
  };
}
